import React from 'react';
import { ArrowLeft, RefreshCw, Calendar, FileText } from 'lucide-react';
import { SurveySummary } from './SurveySummary';
import type { CategoryScore } from '../../types/survey-student';

interface SurveyResultsViewProps {
  scores: CategoryScore[];
  totalResponses: number;
  completionPercentage: number;
  submissionState: 'idle' | 'submitting' | 'calculating' | 'completed' | 'error';
  error: string | null;
  onBack: () => void;
  onRestart: () => void;
}

export const SurveyResultsView: React.FC<SurveyResultsViewProps> = ({
  scores,
  totalResponses,
  completionPercentage,
  submissionState,
  error,
  onBack,
  onRestart
}) => {
  const formatDate = (dateString?: string) => {
    const date = new Date(dateString || Date.now());
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCompletionStatus = (percentage: number) => {
    if (percentage >= 100) return { text: 'Complete', color: 'text-emerald-600', bgColor: 'bg-emerald-50' };
    if (percentage >= 75) return { text: 'Mostly Complete', color: 'text-lime-600', bgColor: 'bg-lime-50' };
    if (percentage >= 50) return { text: 'Partially Complete', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    return { text: 'Started', color: 'text-gray-600', bgColor: 'bg-gray-50' };
  };

  const status = getCompletionStatus(completionPercentage);

  return (
    <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onBack}
                  className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  title="Return to survey"
                  aria-label="Return to survey"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <FileText className="w-8 h-8 text-emerald-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Survey Results</h1>
                  <p className="text-gray-600">Detailed analysis of your survey responses</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onRestart}
                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                title="Restart Survey"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Survey Info */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">Completed on:</span>
                  <span>{formatDate()}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Questions answered:</span>
                  <span>{totalResponses}</span>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${status.bgColor} ${status.color}`}>
                {status.text} - {Math.round(completionPercentage)}%
              </div>
            </div>

            {/* Status indicator */}
            {submissionState === 'completed' && scores && scores.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4 inline-block">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-sm font-medium text-emerald-800">
                    Results saved and calculated successfully
                  </span>
                </div>
              </div>
            )}
            
            {submissionState === 'error' && error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 inline-block">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm font-medium text-yellow-800">
                    Using local calculations (backend unavailable)
                  </span>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Completion:</span>
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-emerald-500 to-lime-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(completionPercentage, 100)}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {Math.round(completionPercentage)}%
              </span>
            </div>
          </div>
        </div>

        {/* Survey Summary */}
        <div className="bg-white rounded-xl shadow-lg">
          <SurveySummary
            scores={scores}
            backgroundData={{
              name: 'Survey User',
              email: '',
              classLevel: '',
              hometown: '',
              firstLanguage: '',
              englishProficiency: 0
            }}
            onRestart={onRestart}
          />
        </div>
    </div>
  );
};